import { Grid, Text } from '@mantine/core';
import { SessionTypeCard } from './SessionTypeCard';
import { SessionType } from '@pages/sessions/new/types';
import { getProductIconUrl, PRODUCT_ICONS } from '../../types/contants';
import { useMemo } from 'react';

export type SessionTypeProductSummary = {
  id: number;
  name: string;
  image: string | null;
  isActive: boolean;
  untilDate: string | null;
  category: string | null;
};

interface SessionTypeSelectorProps {
  selectedType: SessionType | null;
  onSelect: (type: SessionType) => void;
  scenarioProducts: SessionTypeProductSummary[];
  experienceProducts: SessionTypeProductSummary[];
  loading?: boolean;
}

const PRODUCT_NAME_TO_ICON_KEY: Record<string, keyof typeof PRODUCT_ICONS> = {
  'Augmented Performance': 'performance',
  'Augmented Exposure': 'exposure',
  'Augmented Wellbeing': 'wellbeing',
  'Augmented Psychology': 'psychology',
  'Augmented Mentalization': 'mentalization',
  'Augmented Situations': 'situations',
  'Augmented Corporate': 'corporate',
} as const;

function getBrandLogosFromProducts(
  products: SessionTypeProductSummary[]
): Array<{ url: string; isActive: boolean; name: string }> {
  return products
    .map(product => {
      const iconKey = PRODUCT_NAME_TO_ICON_KEY[product.name];
      if (!iconKey) {
        return null;
      }
      return {
        url: getProductIconUrl(iconKey),
        isActive: product.isActive,
        name: product.name,
      };
    })
    .filter(
      (item): item is { url: string; isActive: boolean; name: string } =>
        item !== null
    )
    .sort((a, b) => {
      // Prima attivi, poi non attivi
      if (a.isActive && !b.isActive) return -1;
      if (!a.isActive && b.isActive) return 1;
      return 0;
    });
}

export function SessionTypeSelector({
  selectedType,
  onSelect,
  scenarioProducts,
  experienceProducts,
  loading,
}: SessionTypeSelectorProps) {
  const scenarioActiveCount = scenarioProducts.filter(p => p.isActive).length;
  const experienceActiveCount = experienceProducts.filter(
    p => p.isActive
  ).length;

  const scenarioDisabled = !loading && scenarioActiveCount === 0;
  const experienceDisabled = !loading && experienceActiveCount === 0;

  const scenarioBrandLogos = useMemo(
    () => getBrandLogosFromProducts(scenarioProducts),
    [scenarioProducts]
  );

  const experienceBrandLogos = useMemo(
    () => getBrandLogosFromProducts(experienceProducts),
    [experienceProducts]
  );

  return (
    <Grid gutter='md' mt='md'>
      <Grid.Col span={6}>
        <SessionTypeCard
          icon='/experince.png'
          title='Esperienza'
          description="Crea un'esperienza immersiva personalizzata"
          isSelected={selectedType === 'esperienza'}
          onClick={() => onSelect('esperienza')}
          disabled={experienceDisabled}
          disabledReason='Attiva almeno un prodotto Augmented Life Experience per configurare un&#39;esperienza.'
          brandLogos={experienceBrandLogos}
          footer={
            <Text size='xs' c='dimmed'>
              {experienceActiveCount} su {experienceProducts.length} abbonamenti
              attivi
            </Text>
          }
        />
      </Grid.Col>
      <Grid.Col span={6}>
        <SessionTypeCard
          icon='/scenario.png'
          title='Scenario'
          description='Configura uno scenario VR personalizzato'
          isSelected={selectedType === 'scenario'}
          onClick={() => onSelect('scenario')}
          disabled={scenarioDisabled}
          disabledReason='Attiva Augmented Exposure o Augmented Performance per configurare uno scenario.'
          brandLogos={scenarioBrandLogos}
          footer={
            <Text size='xs' c='dimmed'>
              {scenarioActiveCount} su {scenarioProducts.length} abbonamenti
              attivi
            </Text>
          }
        />
      </Grid.Col>
    </Grid>
  );
}
